import { cn } from "@/lib/utils";
import { size } from "lodash";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { toast } from "sonner";


export const useCarstenTheme = () => {
  const { variant } = useTheme();
  const sidebar = useSidebar()
  const isDark = variant === 'dark';

  return {
    sidebar: {
      icon: {
        // Icon Background: #F7F7F7
        // Icon Background hover +text: #F7F7F7
        // Icon Background: #022213
        // Icon Background hover +text: #022213

        default: isDark ? '!text-[#CECECE] !bg-[#1e392b]' : '!text-[#78887E] !bg-[#F7F7F7]',
        hover: isDark ? '!hover:text-white' : '!hover:text-black',
      },
      // Text color: #78887E
      // Text color active: 
      // Text color: #CECECE
      // Text color active: #FFF

      text: {
        default: isDark ? '!text-[#CECECE]' : '!text-[#78887E]',
        hover: isDark ? '!text-[#CECECE]' : '!text-[#78887E]',
        active: isDark ? '!text-[#000]' : '!text-[#78887E]',
      },
      background: {
        default: isDark ? '' : '',
        hover: isDark ? '!hover:bg-[#1E392B]' : '!hover:bg-[#F7F7F7]',
      },
    },
    rightMenu: {
      icon: {
        default: isDark ? '!text-[#CECECE]' : '!text-[#78887E]',
        hover: isDark ? '!hover:text-white' : '!hover:text-black',
      },
    },
  };
};
import {
  ChainlitContext,
  ClientError,
  ThreadHistory,
  // sessionIdState,
  threadHistoryState,
  useChatInteract,
  useChatMessages,
  useChatSession,
  useConfig,
} from "@chainlit/react-client";

import Alert from "@/components/Alert";
import { Loader } from "@/components/Loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { Translator } from "../i18n";
import ShareDialog from "@/components/share/ShareDialog";
import ThreadOptions from "./ThreadOptions";
import { Chat } from "../icons/Chat";
import { useTheme } from "../ThemeProvider";

interface ThreadListProps {
  threadHistory?: ThreadHistory;
  error?: string;
  isFetching: boolean;
  isLoadingMore: boolean;
}

export function ThreadList({
  threadHistory,
  error,
  isFetching,
  isLoadingMore,
}: ThreadListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { idToResume } = useChatSession();
  const { clear } = useChatInteract();
  const { threadId: currentThreadId } = useChatMessages();
  const [threadIdToDelete, setThreadIdToDelete] = useState<string>();
  const [threadIdToRename, setThreadIdToRename] = useState<string>();
  const [threadNewName, setThreadNewName] = useState<string>();
  const setThreadHistory = useSetRecoilState(threadHistoryState);
  const apiClient = useContext(ChainlitContext);
  const { config } = useConfig();
  const dataPersistence = config?.dataPersistence;
  const threadSharingReady = Boolean((config as any)?.threadSharing);
  // sessionId not needed here
  const theme = useTheme()
  // Share thread state
  const [threadIdToShare, setThreadIdToShare] = useState<string | undefined>();
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  // Share dialog state is centralized in ShareDialog; we only track which thread to share

  const handleShareThread = (threadId: string) => {
    if (!threadSharingReady) return;
    setThreadIdToShare(threadId);
    setIsShareDialogOpen(true);
    // ShareDialog handles its own internal state; we just open it
  };

  const sortedTimeGroupKeys = useMemo(() => {
    if (!threadHistory?.timeGroupedThreads) return [];
    const fixedOrder = [
      "Today",
      "Yesterday",
      "Previous 7 days",
      "Previous 30 days",
    ];
    return Object.keys(threadHistory.timeGroupedThreads).sort((a, b) => {
      const aIndex = fixedOrder.indexOf(a);
      const bIndex = fixedOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [threadHistory?.timeGroupedThreads]);

  if (isFetching || (!threadHistory?.timeGroupedThreads && isLoadingMore)) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error" className="m-3">
        {error}
      </Alert>
    );
  }

  if (!threadHistory || size(threadHistory?.timeGroupedThreads) === 0) {

    // this return an empty tag bc carsten asked for it to be deleted
    // the code will stay here in case we needed to show it again
    return <></>
    return (
      <Alert variant="info" className={`m-3  `}>
        <Translator path="threadHistory.sidebar.empty" />
      </Alert>
    );
  }

  const handleDeleteThread = async () => {
    if (!threadIdToDelete) return;
    if (
      threadIdToDelete === idToResume ||
      threadIdToDelete === currentThreadId
    ) {
      clear();
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    toast.promise(apiClient.deleteThread(threadIdToDelete), {
      loading: (
        <Translator path="threadHistory.thread.actions.delete.inProgress" />
      ),
      success: () => {
        setThreadHistory((prev) => ({
          ...prev,
          threads: prev?.threads?.filter((t) => t.id !== threadIdToDelete),
        }));
        navigate("/");
        return (
          <Translator path="threadHistory.thread.actions.delete.success" />
        );
      },
      error: (err) => {
        if (err instanceof ClientError) {
          return <span>{err.message}</span>;
        } else {
          return <span></span>;
        }
      },
    });
  };

  const handleRenameThread = () => {
    if (!threadIdToRename || !threadNewName) return;

    toast.promise(apiClient.renameThread(threadIdToRename, threadNewName), {
      loading: (
        <Translator path="threadHistory.thread.actions.rename.inProgress" />
      ),
      success: () => {
        setThreadNewName(undefined);
        setThreadIdToRename(undefined);
        setThreadHistory((prev) => {
          const next = {
            ...prev,
            threads: prev?.threads ? [...prev.threads] : undefined,
          };
          const threadIndex = next.threads?.findIndex(
            (t) => t.id === threadIdToRename,
          );
          if (typeof threadIndex === "number" && next.threads) {
            next.threads[threadIndex] = {
              ...next.threads[threadIndex],
              name: threadNewName,
            };
          }
          return next;
        });
        return (
          <div>
            <Translator path="threadHistory.thread.actions.rename.success" />
          </div>
        );
      },
      error: (err) => {
        if (err instanceof ClientError) {
          return <span>{err.message}</span>;
        } else {
          return <span></span>;
        }
      },
    });
  };

  const getTimeGroupLabel = (group: string) => {
    const labels = {
      Today: <Translator path="threadHistory.sidebar.timeframes.today" />,
      Yesterday: (
        <Translator path="threadHistory.sidebar.timeframes.yesterday" />
      ),
      "Previous 7 days": (
        <Translator path="threadHistory.sidebar.timeframes.previous7days" />
      ),
      "Previous 30 days": (
        <Translator path="threadHistory.sidebar.timeframes.previous30days" />
      ),
    };
    return labels[group as keyof typeof labels] || group;
  };
  const THEME_SIDEBAR_HEADER_CARSTEN = useCarstenTheme()
  return (
    <>
      <AlertDialog
        open={!!threadIdToDelete}
        onOpenChange={() => setThreadIdToDelete(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Translator path="threadHistory.thread.actions.delete.title" />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Translator path="threadHistory.thread.actions.delete.description" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-2 sm:gap-0">


            <AlertDialogCancel style={{
              backgroundColor: "#fff",

              border: "3px solid #01FF48",
            }} className="mt-0">
              <Translator style={{ color: "#000", fontWeight: "bold" }}
                path="common.actions.cancel" />
            </AlertDialogCancel>
            <AlertDialogAction style={{
              backgroundColor: "#01FF48",
            }} onClick={handleDeleteThread}>
              <Translator style={{ color: "#000", fontWeight: "bold" }}
                path="common.actions.confirm" />
            </AlertDialogAction>


          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={!!threadIdToRename}
        onOpenChange={() => setThreadIdToRename(undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Translator path="threadHistory.thread.actions.rename.title" />
            </DialogTitle>
            <DialogDescription>
              <Translator path="threadHistory.thread.actions.rename.description" />
            </DialogDescription>
          </DialogHeader>
          <div className="my-6">
            <Label htmlFor="name" className="text-right">
              <Translator path="threadHistory.thread.actions.rename.form.name.label" />
            </Label>
            <Input
              id="name"
              required
              value={threadNewName}
              onChange={(e) => setThreadNewName(e.target.value)}
              placeholder={t(
                "threadHistory.thread.actions.rename.form.name.placeholder",
              )}
              autoFocus
            />
          </div>
          <DialogFooter>


            <Button
              variant="outline"
              style={{
                backgroundColor: "#fff",

                border: "3px solid #01FF48",
              }}
              onClick={() => setThreadIdToRename(undefined)}
            >
              <Translator
                style={{ color: "#000", fontWeight: "bold" }}
                path="common.actions.cancel"
              />
            </Button>
            <Button
              variant="outline"
              style={{
                backgroundColor: "#01FF48",
              }}
              onClick={handleRenameThread}
              id="confirm"
            >
              <Translator
                style={{ color: "#000", fontWeight: "bold" }}
                path="common.actions.confirm"
              />
            </Button>

          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ShareDialog
        open={isShareDialogOpen}
        onOpenChange={(open) => {
          setIsShareDialogOpen(open);
          if (!open) {
            setThreadIdToShare(undefined);
          }
        }}
        threadId={threadIdToShare || null}
      />
      <TooltipProvider delayDuration={300}>
        {sortedTimeGroupKeys.map((group) => {
          const items = threadHistory!.timeGroupedThreads![group];
          return (
            <SidebarGroup key={group}>
              <SidebarGroupLabel>{getTimeGroupLabel(group)}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map((thread) => {
                    const isResumed = idToResume === thread.id &&
                      !threadHistory!.currentThreadId;
                    const isSelected = isResumed ||
                      threadHistory!.currentThreadId === thread.id;
                    return (
                      <SidebarMenuItem
                        key={thread.id}
                        id={`thread-${thread.id}`}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link to={isResumed ? "" : `/thread/${thread.id}`} className={`${THEME_SIDEBAR_HEADER_CARSTEN.sidebar.background.default} ${THEME_SIDEBAR_HEADER_CARSTEN.sidebar.background.hover}`}>
                              <SidebarMenuButton
                                isActive={isSelected}
                                className={`relative truncate h-9 group/thread `}
                              >
                                <span
                                  style={{
                                    marginRight: "8px",
                                    padding: "5px",
                                  }}
                                  className={`rounded ${THEME_SIDEBAR_HEADER_CARSTEN.sidebar.icon.default} ${THEME_SIDEBAR_HEADER_CARSTEN.sidebar.icon.hover}`}
                                >
                                  <Chat className="" />
                                </span>
                                <span className={`${THEME_SIDEBAR_HEADER_CARSTEN.sidebar.text.default} ${THEME_SIDEBAR_HEADER_CARSTEN.sidebar.text.hover} ${THEME_SIDEBAR_HEADER_CARSTEN.sidebar.text.active}`}>
                                  {thread.name || (
                                    <Translator className={`${THEME_SIDEBAR_HEADER_CARSTEN.sidebar.text.default} ${THEME_SIDEBAR_HEADER_CARSTEN.sidebar.text.hover} ${THEME_SIDEBAR_HEADER_CARSTEN.sidebar.text.active}`} path="threadHistory.thread.untitled" />
                                  )}
                                </span>

                                <div
                                  className={cn(
                                    "absolute w-10 bottom-0 top-0 right-0 bg-gradient-to-l from-[hsl(var(--sidebar-background))] to-transparent",
                                  )}
                                />
                                <ThreadOptions
                                  onDelete={() =>
                                    setThreadIdToDelete(thread.id)}
                                  onRename={() => {
                                    setThreadIdToRename(thread.id);
                                    setThreadNewName(thread.name);
                                  }}
                                  // onShare={dataPersistence
                                  //   ? () => handleShareThread(thread.id)
                                  //   : undefined}
                                  className={cn(
                                    "absolute z-20 bottom-0 top-0 right-0 bg-sidebar-accent hover:bg-sidebar-accent hover:text-primary flex opacity-0 group-hover/thread:opacity-100",
                                    isSelected &&
                                    "bg-sidebar-accent opacity-100",
                                  )}
                                />
                              </SidebarMenuButton>
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="right" align="center">
                            <p>{thread.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </TooltipProvider>
      {isLoadingMore
        ? (
          <div className="flex items-center justify-center p-2">
            <Loader />
          </div>
        )
        : null}
    </>
  );
}
